/**
 * React Query Client Configuration
 * 
 * Centralized configuration for TanStack Query (React Query)
 * Provides optimized caching, background updates, and error handling
 */

import { QueryClient } from '@tanstack/react-query';

/**
 * Default query client configuration
 * 
 * Optimized for campaign data with appropriate cache times and retry logic
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Cache data for 5 minutes by default
      staleTime: 5 * 60 * 1000, // 5 minutes
      
      // Keep data in cache for 10 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
      
      // Retry failed requests up to 3 times
      retry: (failureCount, error) => {
        // Don't retry on 4xx errors (client errors)
        if (error instanceof Error && 'status' in error) {
          const status = (error as any).status;
          if (status >= 400 && status < 500) {
            return false;
          }
        }
        return failureCount < 3;
      },
      
      // Retry delay with exponential backoff
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      
      // Refetch on window focus for fresh data
      refetchOnWindowFocus: true,
      
      // Refetch on reconnect
      refetchOnReconnect: true,
      
      // Don't refetch on mount if data is fresh
      refetchOnMount: 'always',
    },
    mutations: {
      // Retry mutations once on failure
      retry: 1,
      
      // Retry delay for mutations
      retryDelay: 1000,
    },
  },
});

/**
 * Query keys for campaign-related queries
 * 
 * Centralized query key management for consistent caching
 */
export const campaignQueryKeys = {
  // Campaign list queries
  campaigns: {
    all: ['campaigns'] as const,
    lists: () => [...campaignQueryKeys.campaigns.all, 'list'] as const,
    list: (filters: Record<string, any>, pagination: Record<string, any>) => 
      [...campaignQueryKeys.campaigns.lists(), { filters, pagination }] as const,
  },
  
  // Campaign detail queries
  campaign: {
    all: ['campaign'] as const,
    details: () => [...campaignQueryKeys.campaign.all, 'detail'] as const,
    detail: (slug: string) => [...campaignQueryKeys.campaign.details(), slug] as const,
  },
  
  // Campaign statistics
  stats: {
    all: ['campaign-stats'] as const,
  },
  
  // Related campaigns
  related: {
    all: ['related-campaigns'] as const,
    list: (slug: string, limit: number) => 
      [...campaignQueryKeys.related.all, slug, limit] as const,
  },
  
  // Social features
  social: {
    all: ['campaign-social'] as const,
    favorites: (campaignId: string) => 
      [...campaignQueryKeys.social.all, 'favorites', campaignId] as const,
    bookmarks: (campaignId: string) => 
      [...campaignQueryKeys.social.all, 'bookmarks', campaignId] as const,
    shares: (slug: string) => 
      [...campaignQueryKeys.social.all, 'shares', slug] as const,
  },
  
  // Donation timeline
  timeline: {
    all: ['donation-timeline'] as const,
    list: (slug: string, page: number, limit: number) =>
      [...campaignQueryKeys.timeline.all, slug, page, limit] as const,
  },

  // Campaign categories
  categories: {
    all: ['campaign-categories'] as const,
  },

  // Discovery filter options (live counts from /api/campaigns/filters)
  filters: {
    all: ['campaign-filters'] as const,
  },

  // Discovery campaign list (separate key space for the enhanced API)
  discovery: {
    all: ['campaign-discovery'] as const,
    lists: () => [...campaignQueryKeys.discovery.all, 'list'] as const,
    list: (filters: Record<string, unknown>, pagination: Record<string, unknown>) =>
      [...campaignQueryKeys.discovery.lists(), { filters, pagination }] as const,
  },

  // Quick Win Bundle -- Funding tiers
  tiers: {
    all: ['campaign-tiers'] as const,
    list: (slug: string) => ['campaign-tiers', 'public', slug] as const,
    ownerList: (campaignId: string) => ['campaign-tiers', 'owner', campaignId] as const,
  },

  // Quick Win Bundle -- Impact milestones
  milestones: {
    all: ['campaign-milestones'] as const,
    list: (slug: string) => ['campaign-milestones', 'public', slug] as const,
    ownerList: (campaignId: string) => ['campaign-milestones', 'owner', campaignId] as const,
  },

  // Quick Win Bundle -- Campaign updates
  updates: {
    all: ['campaign-updates'] as const,
    list: (slug: string, page: number, limit: number) =>
      ['campaign-updates', 'public', slug, page, limit] as const,
    ownerList: (campaignId: string) => ['campaign-updates', 'owner', campaignId] as const,
  },

  // Quick Win Bundle -- Donor recognition board
  donorBoard: {
    all: ['donor-board'] as const,
    detail: (slug: string) => ['donor-board', slug] as const,
  },
} as const;

/**
 * Query keys for curated cause queries
 */
export const causeQueryKeys = {
  causes: {
    all: ['causes'] as const,
    curated: () => [...causeQueryKeys.causes.all, 'curated'] as const,
    detail: (slug: string) => [...causeQueryKeys.causes.all, 'detail', slug] as const,
  },
} as const;

/**
 * Query keys for crowdfunding (Launchpad) queries
 * Uses 'cf-' prefix to avoid collision with campaign query keys
 */
export const crowdfundingQueryKeys = {
  // Public project queries
  projects: {
    all: ['cf-projects'] as const,
    lists: () => [...crowdfundingQueryKeys.projects.all, 'list'] as const,
    list: (filters: Record<string, unknown>, pagination: Record<string, unknown>) =>
      [...crowdfundingQueryKeys.projects.lists(), { filters, pagination }] as const,
  },

  // Project detail
  project: {
    all: ['cf-project'] as const,
    details: () => [...crowdfundingQueryKeys.project.all, 'detail'] as const,
    detail: (slug: string) => [...crowdfundingQueryKeys.project.details(), slug] as const,
  },

  // Reward tiers
  rewards: {
    all: ['cf-rewards'] as const,
    public: (slug: string) => ['cf-rewards', 'public', slug] as const,
    owner: (projectId: string) => ['cf-rewards', 'owner', projectId] as const,
  },

  // Milestones
  milestones: {
    all: ['cf-milestones'] as const,
    public: (slug: string) => ['cf-milestones', 'public', slug] as const,
    owner: (projectId: string) => ['cf-milestones', 'owner', projectId] as const,
  },

  // Project updates
  updates: {
    all: ['cf-updates'] as const,
    public: (slug: string, page: number) => ['cf-updates', 'public', slug, page] as const,
    owner: (projectId: string) => ['cf-updates', 'owner', projectId] as const,
  },

  // Comments
  comments: {
    all: ['cf-comments'] as const,
    list: (slug: string, page: number) => ['cf-comments', slug, page] as const,
  },

  // Backers (public timeline)
  backers: {
    all: ['cf-backers'] as const,
    list: (slug: string, page: number) => ['cf-backers', slug, page] as const,
  },

  // Platform stats
  stats: {
    all: ['cf-stats'] as const,
  },

  // Public categories (for pickers/dropdowns)
  categories: {
    all: ['cf-categories'] as const,
  },

  // Creator queries
  creator: {
    all: ['cf-creator'] as const,
    projects: (filters: Record<string, unknown>) =>
      [...crowdfundingQueryKeys.creator.all, 'projects', { filters }] as const,
    stats: () => [...crowdfundingQueryKeys.creator.all, 'stats'] as const,
    projectStats: (projectId: string) =>
      [...crowdfundingQueryKeys.creator.all, 'project-stats', projectId] as const,
  },

  // Backer pledge queries
  pledges: {
    all: ['cf-pledges'] as const,
    lists: () => [...crowdfundingQueryKeys.pledges.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...crowdfundingQueryKeys.pledges.lists(), { filters }] as const,
    detail: (pledgeId: string) =>
      [...crowdfundingQueryKeys.pledges.all, 'detail', pledgeId] as const,
  },

  // Admin queries
  admin: {
    all: ['cf-admin'] as const,
    projects: (filters: Record<string, unknown>) =>
      [...crowdfundingQueryKeys.admin.all, 'projects', { filters }] as const,
    project: (projectId: string) =>
      [...crowdfundingQueryKeys.admin.all, 'project', projectId] as const,
    escrow: (projectId: string) =>
      [...crowdfundingQueryKeys.admin.all, 'escrow', projectId] as const,
    categories: () =>
      [...crowdfundingQueryKeys.admin.all, 'categories'] as const,
  },
} as const;

/**
 * Query keys for halal directory listing queries
 * Uses 'lst-' prefix to avoid collision with other slices' query keys.
 *
 * ⚠️ Every read hook MUST append langKeySegment(lang) to these keys. Without it
 * a cached Arabic response is served to an English user once translated content
 * lands. See entities/listing/hooks/useListingQueries.ts.
 */
export const listingQueryKeys = {
  // Public listing list queries
  listings: {
    all: ['lst-listings'] as const,
    lists: () => [...listingQueryKeys.listings.all, 'list'] as const,
    list: (filters: Record<string, unknown>, pagination: Record<string, unknown>) =>
      [...listingQueryKeys.listings.lists(), { filters, pagination }] as const,
  },

  // Listing detail
  listing: {
    all: ['lst-listing'] as const,
    details: () => [...listingQueryKeys.listing.all, 'detail'] as const,
    detail: (slug: string) => [...listingQueryKeys.listing.details(), slug] as const,
  },

  // Map-viewport pins.
  //
  // ⚠️ The `bounds` in this key MUST already be rounded by the caller. A map
  // emits a new viewport on every frame of a pan, and raw float bounds would
  // mint a cache entry per frame — the cache would never hit and every pan
  // would be a network request.
  mapPins: {
    all: ['lst-map-pins'] as const,
    list: (bounds: Record<string, unknown>, filters: Record<string, unknown>) =>
      [...listingQueryKeys.mapPins.all, { bounds, filters }] as const,
  },

  // The category tile row. Slug-keyed and translated on the client, so unlike
  // the list/detail keys this one carries NO langKeySegment — the payload is
  // language-agnostic (the label comes from the i18n bundle, not the API).
  categories: {
    all: ['lst-categories'] as const,
  },
} as const;

/**
 * Query keys for halal directory reviews & ratings (doc 11).
 * Uses the same 'lst-' family so it invalidates cleanly alongside listing keys.
 *
 * ⚠️ The public list key MUST append langKeySegment(lang) — reviews are shown in the
 * language they were written in, but the surrounding chrome (author labels, "Edited")
 * is translated, so a cached Arabic-chrome page must not be served to an English user.
 */
export const listingReviewQueryKeys = {
  reviews: {
    all: ['lst-reviews'] as const,
    list: (slug: string, page: number) => ['lst-reviews', 'list', slug, page] as const,
    mine: (slug: string) => ['lst-reviews', 'mine', slug] as const,
  },
} as const;

/**
 * Query keys for halal directory business-profile claims (doc 10).
 * Uses the 'lst-' family. A claim is per-user and never public, so these are only
 * ever fetched for the signed-in claimant.
 */
export const listingClaimQueryKeys = {
  claims: {
    all: ['lst-claims'] as const,
    mine: () => ['lst-claims', 'mine'] as const,
  },
} as const;

/**
 * Query keys for changelog queries
 */
export const changelogQueryKeys = {
  // Public changelog list
  entries: {
    all: ['changelog'] as const,
    lists: () => [...changelogQueryKeys.entries.all, 'list'] as const,
    list: (filters: Record<string, unknown>, pagination: Record<string, unknown>) =>
      [...changelogQueryKeys.entries.lists(), { filters, pagination }] as const,
  },

  // Single changelog entry (by slug)
  entry: {
    all: ['changelog-entry'] as const,
    details: () => [...changelogQueryKeys.entry.all, 'detail'] as const,
    detail: (slug: string) => [...changelogQueryKeys.entry.details(), slug] as const,
  },
} as const;

/**
 * Query keys for user platform context
 */
export const userContextQueryKeys = {
  all: ['user-context'] as const,
  context: () => [...userContextQueryKeys.all, 'platform'] as const,
} as const;

/**
 * Query keys for user notification queries
 */
export const notificationQueryKeys = {
  notifications: {
    all: ['notifications'] as const,
    lists: () => [...notificationQueryKeys.notifications.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...notificationQueryKeys.notifications.lists(), { filters }] as const,
    unreadCount: () =>
      [...notificationQueryKeys.notifications.all, 'unread-count'] as const,
  },
} as const;

/**
 * Query keys for admin notification queries (separate namespace)
 */
export const adminNotificationQueryKeys = {
  notifications: {
    all: ['admin-notifications'] as const,
    lists: () => [...adminNotificationQueryKeys.notifications.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...adminNotificationQueryKeys.notifications.lists(), { filters }] as const,
    unreadCount: () =>
      [...adminNotificationQueryKeys.notifications.all, 'unread-count'] as const,
  },
} as const;

/**
 * Default query options for different types of data
 */
export const queryOptions = {
  // Campaign list - moderate caching since it changes frequently
  campaignList: {
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
  },
  
  // Campaign details - longer caching since individual campaigns change less frequently
  campaignDetail: {
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  },
  
  // Campaign stats - moderate caching
  campaignStats: {
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes
  },
  
  // Related campaigns - moderate caching
  relatedCampaigns: {
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes
  },
  
  // Social features - shorter caching since they change frequently
  socialFeatures: {
    staleTime: 1 * 60 * 1000, // 1 minute
    gcTime: 5 * 60 * 1000, // 5 minutes
  },
  
  // Donation timeline - moderate caching
  donationTimeline: {
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  },

  // Campaign categories - long caching (rarely change)
  campaignCategories: {
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 60 * 60 * 1000, // 1 hour
  },

  // Curated causes - moderate caching
  curatedCauses: {
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  },

  // User platform context - moderate caching (limits change infrequently)
  userContext: {
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes
  },

  // Quick Win Bundle -- Funding tiers (owner manages infrequently)
  fundingTiers: {
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes
  },

  // Quick Win Bundle -- Impact milestones (change only on donations)
  impactMilestones: {
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes
  },

  // Quick Win Bundle -- Campaign updates (moderate frequency)
  campaignUpdates: {
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  },

  // Quick Win Bundle -- Donor board (server caches 60s)
  donorBoard: {
    staleTime: 1 * 60 * 1000, // 1 minute (match server cache TTL)
    gcTime: 5 * 60 * 1000, // 5 minutes
  },

  // Notification list -- short cache since SSE keeps it fresh
  notifications: {
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes
  },

  // Notification unread count -- badge should be responsive
  notificationUnreadCount: {
    staleTime: 15 * 1000, // 15 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes
  },

  // Crowdfunding project list
  crowdfundingList: {
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
  },

  // Crowdfunding project detail
  crowdfundingDetail: {
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  },

  // Crowdfunding platform stats
  crowdfundingStats: {
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes
  },

  // Crowdfunding sub-resources (rewards, milestones, updates, comments)
  crowdfundingSubResource: {
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes
  },

  // Directory listing list -- a moderated directory changes slowly (a new
  // listing must clear a human moderation queue first), so a longer staleTime
  // than a campaign list is correct here, not lazy.
  listingList: {
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes
  },

  // Directory listing detail -- a business's phone number and address are the
  // slowest-changing data in the product.
  listingDetail: {
    staleTime: 15 * 60 * 1000, // 15 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  },

  // Map pins. A pin is a coordinate and a name -- the least volatile data we
  // hold -- so this caches hard. The point is that panning BACK to a viewport
  // the user already visited must not re-hit a public, rate-limited endpoint
  // whose limiter fails open when Redis is down (00-MASTER §5).
  listingMapPins: {
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  },

  // Directory categories. Admin-managed and changed about monthly, so this
  // caches hard — the tile row must not re-fetch on every visit to the
  // discovery page, and a public endpoint whose limiter fails open (00-MASTER
  // §5) should not be hit for data that barely moves.
  listingCategories: {
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 60 * 60 * 1000, // 1 hour
  },

  // Directory reviews. A moderated review list changes slowly (new reviews clear a
  // sensitive-content gate, edits re-run it), so a short-but-not-instant staleTime
  // is right: fresh enough that a just-posted review appears on revisit, cached
  // enough that scrolling back to a listing does not re-hit a rate-limited endpoint.
  listingReviews: {
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  },

  // Crowdfunding categories (admin-managed, rarely change)
  crowdfundingCategories: {
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 60 * 60 * 1000, // 1 hour
  },

  // Crowdfunding pledges (backer's own pledges)
  crowdfundingPledges: {
    staleTime: 1 * 60 * 1000, // 1 minute
    gcTime: 5 * 60 * 1000, // 5 minutes
  },

  // Crowdfunding creator stats
  crowdfundingCreatorStats: {
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  },
} as const;
